import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/Layout';
import './JobApplicationPage.css';

import { API_BASE_URL } from '../config/api';

const JobApplicationPage = () => {
  const { jobSlug } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Job titles mapped by slug
  const jobTitlesBySlug = {
    "RA-electronics-design-engineer": "Research Associate I - Electronics Design Engineer",
    "SRF-electronics-design-engineer": "Senior Research Fellow (SRF) - Electronics Design Engineer", 
    "JRF-electronics-design-engineer": "Junior Research Fellow (JRF) - Electronics Design Engineer",
    "RA-ML-AI-engineer-livestock-AI": "Research Associate I - ML/AI Engineer - Livestock AI",
    "SRF-ML-AI-engineer-livestock-AI": "Senior Research Fellow (SRF) - ML/AI Engineer - Livestock AI",
    "JRF-ML-AI-engineer-livestock-AI": "Junior Research Fellow (JRF) - ML/AI Engineer - Livestock AI",
    "RA-microbiologist-livestock-AI": "Research Associate I - Microbiologist - Livestock AI",
    "SRF-microbiologist-livestock-AI": "Senior Research Fellow (SRF) - Microbiologist - Livestock AI",
    "JRF-biotechnologist-livestock-AI": "Junior Research Fellow - Biotechnologist - Livestock AI",
    "project-assistant-microbiology": "Project Assistant - Microbiology / Biotechnology",
    "project-assistant-electronics": "Project Assistant - Electronics Design Engineer",
    "project-assistant-ai": "Project Assistant - ML/AI Engineer - Livestock AI"
  };

  const jobTitle = jobTitlesBySlug[jobSlug] || "Position Not Specified";

  // Determine job role type from slug
  const getJobRole = () => {
    if (jobSlug.startsWith('RA-')) return 'RA';
    if (jobSlug.startsWith('JRF-')) return 'JRF';
    if (jobSlug.startsWith('SRF-')) return 'SRF';
    if (jobSlug.startsWith('project-assistant-')) return 'PROJECT_ASSISTANT';
    return 'OTHER';
  };

  const jobRole = getJobRole();

  // Qualification options based on job role
  const qualificationOptions = {
    RA: [
      'PhD (submitted/awarded) in relevant field'
    ],
    JRF: [
      'Postgraduate degree',
      'Graduate in relevant discipline + 2 years of experience'
    ],
    SRF: [
      'M.E/M.Tech + 2 or more years of experience',
      'PhD (pursuing) + 2 or more years of experience'
    ],
    PROJECT_ASSISTANT: [
      'B.Sc. in Relevant Discipline'
    ],
    OTHER: []
  };
//
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    qualification: '',
    graduationYear: '',
    experience: '',
    skills: '',
    coverLetter: '',
    linkedinProfile: '',
    resume: null
  });

  // Dynamic qualification details state
  const [qualificationDetails, setQualificationDetails] = useState({
    collegeName: '',
    courseName: '',
    dateFrom: '',
    dateTo: '',
    yearsOfExperience: '',
    specialization: '',
    companyName: '',
    experienceDateFrom: '',
    experienceDateTo: ''
  });

  const [wordCount, setWordCount] = useState(0);
  const [showWordWarning, setShowWordWarning] = useState(false);
  const [pasteWarning, setPasteWarning] = useState(false);
  const [graduationYearWarning, setGraduationYearWarning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Check if screen is mobile
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine which fields to show based on qualification selection
  const getQualificationFieldType = (qualification) => {
    // Only show dynamic fields for JRF roles
    if (jobRole !== 'JRF') return null;
    
    if (!qualification) return null;
    
    // Education-based qualifications (show college fields)
    const educationQualifications = [
      'Postgraduate degree in Basic Sciences'
    ];
    
    // Experience-based qualifications (show company fields)
    const experienceQualifications = [
      'Graduate in relevant discipline + 2 years of experience'
    ];
    
    if (educationQualifications.includes(qualification)) {
      return 'education';
    } else if (experienceQualifications.includes(qualification)) {
      return 'experience';
    }
    
    return null;
  };

  const qualificationFieldType = getQualificationFieldType(formData.qualification);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle cover letter word count
    if (name === 'coverLetter') {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      const count = value.trim() === '' ? 0 : words.length;
      setWordCount(count);
    }

    // Handle graduation year restriction
    if (name === 'graduationYear') {
      const restrictedYears = [2024, 2025, 2026, 2027, 2028, 2029, 2030];
      const yearNum = parseInt(value, 10);
      if (value.length === 4 && restrictedYears.includes(yearNum)) {
        setGraduationYearWarning(true);
        setTimeout(() => setGraduationYearWarning(false), 4000);
      } else {
        setGraduationYearWarning(false);
      }
    }

    // Reset qualification details when qualification changes
    if (name === 'qualification') {
      setQualificationDetails({
        collegeName: '',
        courseName: '',
        dateFrom: '',
        dateTo: '',
        yearsOfExperience: '',
        specialization: '',
        companyName: '',
        experienceDateFrom: '',
        experienceDateTo: ''
      });
      setFormData(prev => ({ ...prev, graduationYear: '' }));
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQualificationDetailsChange = (e) => {
    const { name, value } = e.target;
    // Reset graduation year when experience selection changes (years shift)
    if (name === 'yearsOfExperience') {
      setFormData(prev => ({ ...prev, graduationYear: '' }));
    }
    setQualificationDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCoverLetterPaste = (e) => {
    e.preventDefault();
    setPasteWarning(true);
    
    // Hide warning after 4 seconds
    setTimeout(() => {
      setPasteWarning(false);
    }, 4000);
    
    return false;
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      resume: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    const requiredFields = ['fullName', 'email', 'phone', 'location', 'qualification', 'experience', 'skills', 'coverLetter', 'linkedinProfile'];
    const emptyFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
    
    if (emptyFields.length > 0) {
      alert("Missing fields: " + emptyFields.join(", "));
      return;
    }
    
    // Validate cover letter word count
    const words = formData.coverLetter.trim().split(/\s+/).filter(word => word.length > 0);
    const count = words.length;
    if (count < 200) {
      setShowWordWarning(true);
      
      // Scroll to cover letter section
      const coverLetterElement = document.getElementById('coverLetter');
      if (coverLetterElement) {
        coverLetterElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      // Hide warning after 3 seconds
      setTimeout(() => {
        setShowWordWarning(false);
      }, 3000);
      
      return;
    }
    
    // Validate graduation year restriction
    const restrictedYears = [2024, 2025, 2026, 2027, 2028, 2029, 2030];
    if (formData.graduationYear && restrictedYears.includes(parseInt(formData.graduationYear, 10))) {
      alert('Candidates who graduated in 2024 or later are not eligible for this position.');
      return;
    }

    // Validate years of experience for JRF roles
    if (jobRole === 'JRF' && !qualificationDetails.yearsOfExperience) {
      alert('Please select your years of experience.');
      return;
    }

    // Validate LinkedIn URL
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/.+/i;
    if (!linkedinRegex.test(formData.linkedinProfile.trim())) {
      alert('Please enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/yourname)');
      return;
    }

    if (!formData.resume) {
      alert('Please upload your resume.');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address.');
      return;
    }
    
    // Validate phone number
    const phoneRegex = /^[+]?[0-9\s\-()]{10,}$/;
    if (!phoneRegex.test(formData.phone)) {
      alert('Please enter a valid phone number.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add all form fields
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('location', formData.location);
      submitData.append('qualification', formData.qualification);
      submitData.append('graduationYear', formData.graduationYear);
      submitData.append('experience', formData.experience);
      submitData.append('skills', formData.skills);
      submitData.append('coverLetter', formData.coverLetter);
      submitData.append('linkedinProfile', formData.linkedinProfile);
      submitData.append('jobTitle', jobTitle);
      submitData.append('jobSlug', jobSlug);
      submitData.append('resume', formData.resume);
      
      // Add qualification details for JRF roles (always send specialization + yearsOfExperience)
      if (jobRole === 'JRF') {
        submitData.append('qualificationDetails', JSON.stringify(qualificationDetails));
      }

      console.log('=== EMAIL ROUTING INFO ===');
      console.log('FROM: aktechnologies2025@gmail.com (AK Technologies)');
      console.log('TO: abishekk0928@gmail.com (Your Email)');
      console.log('BACKEND URL:', API_BASE_URL);
      console.log('REPLY-TO:', formData.email, '(' + formData.fullName + ')');
      console.log('SUBJECT:', `Job Application: ${jobTitle} - ${formData.fullName}`);
      console.log('ATTACHMENT:', formData.resume.name);
      console.log('========================');
      
      console.log('Submitting to backend server...');

      // Submit to backend server
      const response = await fetch(`${API_BASE_URL}/api/job-application`, {
        method: 'POST',
        body: submitData
      });

      const result = await response.json();
      
      console.log('=== BACKEND RESPONSE ===');
      console.log('Success:', result.success);
      console.log('Message:', result.message);
      console.log('Response status:', response.status);
      if (result.messageId) console.log('Email Message ID:', result.messageId);
      console.log('========================');

      if (result.success) {
        setIsSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          location: '',
          qualification: '',
          graduationYear: '',
          experience: '',
          skills: '',
          coverLetter: '',
          linkedinProfile: '',
          resume: null
        });
        
        setWordCount(0);
        setShowWordWarning(false);
        
        // Reset file input
        const fileInput = document.getElementById('resume');
        if (fileInput) fileInput.value = '';
        
      } else {
        console.error('Backend error:', result);
        alert(result.message || 'There was an error submitting your application. Please try again.');
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please make sure the backend server is running and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const restrictedYears = [2024, 2025, 2026, 2027, 2028, 2029, 2030];
  const graduationYearNum = parseInt(formData.graduationYear, 10);
  const isGraduationYearInvalid =
    !formData.graduationYear ||
    formData.graduationYear.length < 4 ||
    restrictedYears.includes(graduationYearNum);

  if (isSubmitted) {
    return (
      <Layout>
        <Helmet>
          <title>Application Submitted - Adept Knowledge Technologies</title>
          <meta name="description" content="Your job application has been submitted successfully to Adept Knowledge Technologies. We will review it and get back to you within 5-7 business days." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <section className="application-success">
          <div className="success-container">
            <div className="success-content">
              <div className="success-icon">✓</div>
              <h1>Application Submitted Successfully!</h1>
              <p>Thank you for your interest in the <strong>{jobTitle}</strong> position.</p>
              <p>We have received your application and will review it shortly. You will hear from us within 5-7 business days.</p>
              <div className="success-actions">
                <button onClick={() => navigate('/careers')} className="back-to-jobs-btn">
                  Back to All Jobs
                </button>
                <button onClick={() => navigate('/')} className="home-btn">
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>Apply for {jobTitle} - Adept Knowledge Technologies</title>
        <meta name="description" content={`Apply for the ${jobTitle} position at Adept Knowledge Technologies. Submit your application for this full-time research role based in Coimbatore, Tamil Nadu.`} />
        <link rel="canonical" href={`https://www.ak-tech.in/careers/${jobSlug}/apply`} />
        <meta property="og:title" content={`Apply for ${jobTitle} - Adept Knowledge Technologies`} />
        <meta property="og:url" content={`https://www.ak-tech.in/careers/${jobSlug}/apply`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <section className="job-application-section">
        <div className="application-container">
          {/* Breadcrumbs */}
          <nav className="breadcrumbs" data-aos="fade-up">
            <Link to="/">Home</Link>
            <span className="separator">/</span>
            <Link to="/careers">Careers</Link>
            <span className="separator">/</span>
            <Link to={`/careers/${jobSlug}`}>{jobTitle}</Link>
            <span className="separator">/</span>
            <span className="current">Apply</span>
          </nav>

          <div className="application-header" data-aos="fade-up">
            <h1>Apply for {jobTitle}</h1>
            <p>Please fill out the form below to submit your application. All fields marked with * are required.</p>
          </div>

          <form className="application-form" onSubmit={handleSubmit} data-aos="fade-up">
            <div className="form-grid">
              {/* Personal Information */}
              <div className="form-section">
                <h3>Personal Information</h3>
                
                <div className="form-group">
                  <label htmlFor="fullName">Full Name <span className="required">*</span></label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email Address <span className="required">*</span></label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone Number <span className="required">*</span></label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      pattern="[0-9+\-\s]+"
                      placeholder="+91 9876543210"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location <span className="required">*</span></label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your location"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="resume">Resume/CV <span className="required">*</span></label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    onChange={handleFileChange}
                    required
                    accept=".pdf,.doc,.docx"
                    className="file-input"
                  />
                  <small>Accepted formats: PDF, DOC, DOCX (Max size: 5MB)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="linkedinProfile">LinkedIn Profile URL <span className="required">*</span></label>
                  <input
                    type="url"
                    id="linkedinProfile"
                    name="linkedinProfile"
                    value={formData.linkedinProfile}
                    onChange={handleInputChange}
                    required
                    placeholder="https://www.linkedin.com/in/yourname"
                  />
                </div>

                {/* Technical Skills - Show on left only when dynamic fields are visible (JRF with qualification selected) AND not on mobile */}
                {qualificationFieldType && !isMobile && (
                  <div className="form-group technical-skills-left">
                    <label htmlFor="skills">Technical Skills <span className="required">*</span></label>
                    <div className="textarea-container skills-textarea">
                      <textarea
                        id="skills"
                        name="skills"
                        value={formData.skills}
                        onChange={handleInputChange}
                        required
                        placeholder="List your technical skills, programming languages, tools, etc."
                      ></textarea>
                    </div>
                  </div>
                )}
              </div>

              {/* Professional Information */}
              <div className="form-section">
                <h3>Professional Information</h3>
                
                <div className="form-group">
                  <label htmlFor="qualification">Highest Qualification <span className="required">*</span></label>
                  {qualificationOptions[jobRole].length > 0 ? (
                    <select
                      id="qualification"
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleInputChange}
                      required
                      className="qualification-select"
                    >
                      <option value="">Select your qualification</option>
                      {qualificationOptions[jobRole].map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="qualification"
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., B.Tech in Computer Science"
                    />
                  )}
                </div>

                {/* Specialization - show when any JRF qualification is selected */}
                {jobRole === 'JRF' && formData.qualification && (
                  <div className="form-group">
                    <label htmlFor="specialization">Specialization <span className="required">*</span></label>
                    <input
                      type="text"
                      id="specialization"
                      name="specialization"
                      value={qualificationDetails.specialization}
                      onChange={handleQualificationDetailsChange}
                      required
                      placeholder="e.g. Microbiology, Biotechnology, Biochemistry"
                    />
                  </div>
                )}

                {/* Years of Experience - JRF only, no fresher option */}
                {jobRole === 'JRF' && (
                  <div className="form-group">
                    <label htmlFor="yearsOfExperience">Years of Experience <span className="required">*</span></label>
                    <select
                      id="yearsOfExperience"
                      name="yearsOfExperience"
                      value={qualificationDetails.yearsOfExperience}
                      onChange={handleQualificationDetailsChange}
                      required
                      className="qualification-select"
                    >
                      <option value="">Select years of experience</option>
                      <option value="Less than 1 year">1 year</option>
                      <option value="1 year">1 to 2 years</option>
                      <option value="2 years">2+ years</option>
                    </select>
                  </div>
                )}

                {/* Year of Graduation - all roles, number input */}
                <div className="form-group" style={{ position: 'relative' }}>
                  <label htmlFor="graduationYear">Year of Graduation <span className="required">*</span></label>
                  <input
                    type="number"
                    id="graduationYear"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g. 2022"
                    min="1990"
                    max="2030"
                    className="qualification-select"
                    style={{ appearance: 'textfield' }}
                  />
                  {graduationYearWarning && (
                    <small style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      color: '#e53935',
                      fontSize: '12px',
                      marginTop: '2px',
                      animation: 'fadeIn 0.3s ease',
                      whiteSpace: 'nowrap',
                      zIndex: 10
                    }}>
                      Candidates who graduated in 2024 or later are not eligible.
                    </small>
                  )}
                </div>

                <div className="form-group work-experience-field">
                  <label htmlFor="experience">Work Experience <span className="required">*</span></label>
                  <div className="textarea-container">
                    <textarea
                      id="experience"
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      placeholder="Describe your relevant work experience, projects, and achievements"
                    ></textarea>
                  </div>
                </div>

                {/* Technical Skills - Show on right by default (when no dynamic fields OR on mobile) */}
                {(!qualificationFieldType || isMobile) && (
                  <div className="form-group">
                    <label htmlFor="skills">Technical Skills <span className="required">*</span></label>
                    <div className="textarea-container skills-textarea">
                      <textarea
                        id="skills"
                        name="skills"
                        value={formData.skills}
                        onChange={handleInputChange}
                        required
                        placeholder="List your technical skills, programming languages, tools, etc."
                      ></textarea>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Cover Letter */}
            <div className="form-section full-width">
              <h3>Cover Letter</h3>
              <div className="form-group">
                <label htmlFor="coverLetter">
                  Why are you interested in this position? <span className="required">*</span>
                  <span className="word-requirement">(Minimum 200 words required)</span>
                  <span className="word-count">{wordCount}/200 words</span>
                </label>
                <div className={`textarea-container ${showWordWarning ? 'word-warning' : ''}`}>
                  <textarea
                    id="coverLetter"
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    onPaste={handleCoverLetterPaste}
                    required
                    placeholder="Tell us why you're interested in this position and how your skills and experience make you a good fit..."
                  ></textarea>
                  {pasteWarning && (
                    <div className="paste-warning-message">
                      🚫 No copy-pasting! Try your own words. We'd rather hear from you, than a chatbot.
                    </div>
                  )}
                  {showWordWarning && (
                    <div className="word-warning-message">
                      Cover letter must contain at least 200 words. Current count: {wordCount} words
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate(`/careers/${jobSlug}`)}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting || isGraduationYearInvalid}
              >
                {isSubmitting ? 'Submitting...' : 'Send Application'}
              </button>
            </div>
          </form>
        </div>
      </section>
      
      {/* Full Page Loader */}
      {isSubmitting && (
        <div className="full-page-loader">
          <div className="loader-content">
            <div className="spinner"></div>
            <p>Submitting your application...</p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default JobApplicationPage;